import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Refresh from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { createRunMetadataFromDoc } from '../browser/FbCol';
import { INSPECTORCLICK_API_BASE_URL, RUN_FILES_TO_LOAD, RUN_FILE_KEY_MAP } from '../appConfig';
import VisualStoryTimeline from '../visualstory/VisualStoryTimeline';
import LogViewer from '../tenant/appesteem/components/LogViewer';

const SIDEBAR_COLLAPSED_WIDTH = 36;
const SIDEBAR_EXPANDED_WIDTH = 300;
const PAGE_SIZE = 100;


function formatDate(value) {
  if (!value) return '';
  let date;
  if (value.toDate) {
    date = value.toDate();
  } else if (typeof value === 'string') {
    date = new Date(value);
  } else {
    return '';
  }
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'completed': return 'success';
    case 'running': case 'queued': return 'info';
    case 'failed': return 'error';
    case 'submitted': return 'primary';
    default: return 'default';
  }
}

export default function ReviewBrowser({
  requestData,
  lastInspectionRunId,
  lastAutowatchRunId,
  readOnlyFromUrl = true,
  isAuthenticated = false,
  getIdToken = null,
  tenantId = null,
  requestId = null,
  currentUser = null,
  initialSearch = null,
  appId = null,
  sha256 = null,
  onAppInfoChange = null,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(isAuthenticated);
  const [searchTerm, setSearchTerm] = useState(initialSearch || '');
  const [activeTab, setActiveTab] = useState(0);
  const [appInfo, setAppInfo] = useState(null);
  const [loadingAppInfo, setLoadingAppInfo] = useState(false);
  const [appInfoError, setAppInfoError] = useState(null);

  // Sidebar list state (authenticated only)
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Selected request state
  const [selectedRequestId, setSelectedRequestId] = useState(requestId || null);
  const [selectedData, setSelectedData] = useState(requestData || null);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [selectedError, setSelectedError] = useState(null);

  // Requeue modal state
  const [requeueModalOpen, setRequeueModalOpen] = useState(false);
  const [requestToRequeue, setRequestToRequeue] = useState(null);
  const [requeueDoAutomation, setRequeueDoAutomation] = useState(false);
  const [requeuePriority, setRequeuePriority] = useState(5);
  const [requeueError, setRequeueError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !requestId) return;
    const ak = requestData?.AccessKey || requestData?.accessKey || null;
    if (ak) {
      if (!sha256 && !appId) {
        const params = new URLSearchParams(window.location.search);
        params.set('accessKey', ak);
        const path = `/review/${encodeURIComponent(requestId)}`;
        const qs = params.toString();
        window.history.replaceState({}, '', qs ? `${path}?${qs}` : path);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeInspectionRunId = selectedData != null
    ? (selectedData.lastInspectionRunId ?? null)
    : (lastInspectionRunId ?? null);
  const activeAutowatchRunId = selectedData != null
    ? (selectedData.lastAutowatchRunId ?? null)
    : (lastAutowatchRunId ?? null);
  const hasVisualStory = Boolean(activeInspectionRunId);
  const hasLog = Boolean(activeAutowatchRunId);
  const hasBothTabs = hasVisualStory && hasLog;

  useEffect(() => {
    if (!isAuthenticated || !tenantId) return;
    setLoadingRequests(true);

    const q = query(
      collection(db, 'inspectionRequest'),
      where('TenantId', '==', tenantId),
      orderBy('CreatedAt', 'desc'),
      limit(PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setRequests(items);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(items.length === PAGE_SIZE);
      setLoadingRequests(false);
    }, (err) => {
      console.error('ReviewBrowser: sidebar snapshot error:', err);
      setLoadingRequests(false);
    });

    return () => unsubscribe();
  }, [isAuthenticated, tenantId]);

  const loadMore = useCallback(async () => {
    if (!lastVisible || !tenantId) return;
    setLoadingRequests(true);
    try {
      const q = query(
        collection(db, 'inspectionRequest'),
        where('TenantId', '==', tenantId),
        orderBy('CreatedAt', 'desc'),
        limit(PAGE_SIZE),
        startAfter(lastVisible)
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setRequests(prev => [...prev, ...items]);
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(items.length === PAGE_SIZE);
    } catch (err) {
      console.error('ReviewBrowser: load more error:', err);
    } finally {
      setLoadingRequests(false);
    }
  }, [lastVisible, tenantId]);

  // Fetch a specific request's data via API (authenticated)
  const handleRequestClick = useCallback(async (reqId) => {
    setSelectedRequestId(reqId);
    setSelectedData(null);
    setSelectedError(null);
    setAppInfoError(null);
    setActiveTab(0);
    if (!getIdToken) return;
    setLoadingSelected(true);
    try {
      const token = await getIdToken();
      const params = new URLSearchParams();
      if (tenantId) params.set('tenantId', tenantId);
      const qs = params.toString();
      const url = `${INSPECTORCLICK_API_BASE_URL}/inspectionrequest/${encodeURIComponent(reqId)}${qs ? `?${qs}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        console.error('ReviewBrowser: API fetch failed', response.status, body);
        setSelectedError(`Failed to load request (${response.status})`);
        return;
      }
      const json = await response.json();
      setSelectedData(json);
      const ak = json.AccessKey || json.accessKey || null;
      if (!sha256 && !appId) {
        const reviewPath = `/review/${encodeURIComponent(reqId)}`;
        const urlParams = new URLSearchParams(window.location.search);
        if (ak) urlParams.set('accessKey', ak);
        else urlParams.delete('accessKey');
        const urlQs = urlParams.toString();
        const reviewUrl = urlQs ? `${reviewPath}?${urlQs}` : reviewPath;
        window.history.replaceState({}, '', reviewUrl);
      }
    } catch (err) {
      console.error('ReviewBrowser: API fetch error', err);
      setSelectedError('Failed to load request');
    } finally {
      setLoadingSelected(false);
    }
  }, [getIdToken, tenantId, sha256, appId]);

  useEffect(() => {
    if (!activeInspectionRunId) return;

    let cancelled = false;
    const loadAppInfo = async () => {
      setLoadingAppInfo(true);
      setAppInfoError(null);
      try {
        const runDoc = await getDoc(doc(db, 'inspectionRun', activeInspectionRunId));
        if (!runDoc.exists()) throw new Error('Inspection run document not found');
        if (cancelled) return;

        const runMetadata = createRunMetadataFromDoc(runDoc);
        const blobPath = runMetadata.blobPath || '';

        const urlResults = await Promise.all(
          RUN_FILES_TO_LOAD.map(async (fileName) => {
            try {
              const fileRef = ref(storage, `${blobPath}/${fileName}`);
              return { fileName, url: await getDownloadURL(fileRef) };
            } catch {
              return { fileName, url: null };
            }
          })
        );
        if (cancelled) return;

        const filePaths = {};
        urlResults.forEach(({ fileName, url }) => {
          if (url) filePaths[RUN_FILE_KEY_MAP[fileName]] = url;
        });

        const info = {
          ...runMetadata.toAppInfo(
            runMetadata.app || selectedData?.appName || selectedData?.AppName || 'Unknown',
            runMetadata.version || selectedData?.appVersion || selectedData?.AppVersion || 'Unknown',
            runMetadata.watchDir || activeInspectionRunId
          ),
          blobPath,
          appId: selectedData?.AppId ?? selectedData?.appId ?? null,
          installerName: selectedData?.InstallerName ?? selectedData?.installerName
            ?? requests.find(r => r.id === selectedRequestId)?.InstallerName ?? null,
          clickimagesPath: `${blobPath}/clickimages`,
          ...filePaths,
          refetchAutoInspect: async () => {
            try {
              const fileRef = ref(storage, `${blobPath}/autoinspect.json`);
              return await getDownloadURL(fileRef);
            } catch {
              return null;
            }
          },
        };
        setAppInfo(info);
        if (onAppInfoChange) onAppInfoChange(info);
      } catch (err) {
        if (!cancelled) {
          console.error('ReviewBrowser: error loading app info', err);
          setAppInfoError(err.message || 'Failed to load inspection data');
        }
      } finally {
        if (!cancelled) setLoadingAppInfo(false);
      }
    };

    loadAppInfo();
    return () => {
      cancelled = true;
      if (onAppInfoChange) onAppInfoChange(null);
    };
  }, [activeInspectionRunId, selectedData]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Requeue handlers ---

  const handleRequeueClick = (req, event) => {
    event.stopPropagation();
    setRequestToRequeue(req);
    setRequeueDoAutomation(req.DoAutomation || false);
    setRequeuePriority(req.Priority || 5);
    setRequeueError(null);
    setRequeueModalOpen(true);
  };

  const handleRequeueConfirm = async () => {
    if (!requestToRequeue || !getIdToken) return;
    try {
      setRequeueError(null);
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const params = new URLSearchParams();
      params.set('doAutomation', requeueDoAutomation);
      params.set('priority', requeuePriority);
      const url = `${INSPECTORCLICK_API_BASE_URL}/inspectionrequest/${encodeURIComponent(requestToRequeue.id)}/requeue?${params}`;
      const response = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) {
        const respBody = await response.json().catch(() => ({}));
        throw new Error(respBody.Error || respBody.error || `Request failed (${response.status})`);
      }
      setRequeueModalOpen(false);
      setRequestToRequeue(null);
      setRequeueDoAutomation(false);
      setRequeuePriority(5);
    } catch (error) {
      console.error('Failed to requeue:', error);
      setRequeueError('Failed to requeue: ' + (error.message || 'Unknown error'));
    }
  };

  // --- Client-side filtering ---

  const filteredRequests = requests.filter(req => {
    if (appId && req.AppId !== appId) return false;
    if (sha256 && req.SHA256 !== sha256) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        req.AppName?.toLowerCase().includes(term) ||
        req.AppVersion?.toLowerCase().includes(term) ||
        req.InstallerName?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  // --- Sidebar rendering ---

  const renderAuthenticatedSidebar = () => (
    <Box sx={{
      width: sidebarOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
      minWidth: sidebarOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
      height: '100%',
      borderRight: 1,
      borderColor: 'divider',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s, min-width 0.2s',
      overflow: 'hidden',
    }}>
      <Box sx={{ display: 'flex', justifyContent: sidebarOpen ? 'flex-end' : 'center', p: 0.5 }}>
        <IconButton size="small" onClick={() => setSidebarOpen(v => !v)}>
          {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      {sidebarOpen && (
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" sx={{ px: 1.5, mb: 0.5 }}>Inspection Requests</Typography>
          <Divider />
          <Box sx={{ px: 1, pt: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
          </Box>
          {loadingRequests && requests.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : requests.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ p: 1.5 }}>
              No requests found
            </Typography>
          ) : (
            <List disablePadding dense>
              {filteredRequests.map((req) => (
                <ListItem key={req.id} disablePadding>
                  <ListItemButton
                    selected={req.id === selectedRequestId}
                    onClick={() => handleRequestClick(req.id)}
                    sx={{ py: 1, px: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500, flex: 1, mr: 1 }}>
                        {req.AppName || 'Unknown App'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getIdToken && (
                          <Tooltip title="Re-Queue">
                            <IconButton
                              size="small"
                              onClick={(e) => handleRequeueClick(req, e)}
                              sx={{ p: 0.25 }}
                            >
                              <Refresh sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Chip
                          label={req.Status || '—'}
                          size="small"
                          color={getStatusColor(req.Status)}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Box>
                    </Box>
                    {req.AppId && (
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25 }}>
                        {req.AppId}
                      </Typography>
                    )}
                    {req.AppVersion && (
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25 }}>
                        {req.AppVersion}
                      </Typography>
                    )}
                    {req.InstallerName && (
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.2 }}>
                        {req.InstallerName}
                      </Typography>
                    )}
                    {req.SHA256 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2, width: '100%' }}>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.45rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {req.SHA256}
                        </Typography>
                        <Tooltip title="Copy SHA256">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(req.SHA256); }}
                            sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
                          >
                            <ContentCopyIcon sx={{ fontSize: '0.65rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25 }}>
                      {formatDate(req.CreatedAt || req.RequestedOn)}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
              {hasMore && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={loadMore}
                    disabled={loadingRequests}
                    sx={{ justifyContent: 'center', py: 1 }}
                  >
                    {loadingRequests ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Typography variant="caption" color="primary">more...</Typography>
                    )}
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          )}
        </Box>
      )}
    </Box>
  );

  const renderAnonymousSidebar = () => (
    <Box sx={{
      width: sidebarOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
      minWidth: sidebarOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
      height: '100%',
      borderRight: 1,
      borderColor: 'divider',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s, min-width 0.2s',
      overflow: 'hidden',
    }}>
      <Box sx={{ display: 'flex', justifyContent: sidebarOpen ? 'flex-end' : 'center', p: 0.5 }}>
        <IconButton size="small" onClick={() => setSidebarOpen(v => !v)}>
          {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      {sidebarOpen && (
        <Box sx={{ p: 1.5, overflow: 'auto', flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Review Summary</Typography>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {requestData?.requestId && (
              <Typography variant="caption" color="text.secondary">
                <strong>Request:</strong> {requestData.requestId}
              </Typography>
            )}
            {requestData?.status && (
              <Chip label={requestData.status} size="small" sx={{ alignSelf: 'flex-start' }} />
            )}
            {requestData?.appName && (
              <Typography variant="caption" color="text.secondary">
                <strong>App:</strong> {requestData.appName}
              </Typography>
            )}
            {requestData?.appVersion && (
              <Typography variant="caption" color="text.secondary">
                <strong>Version:</strong> {requestData.appVersion}
              </Typography>
            )}
            {requestData?.installerName && (
              <Typography variant="caption" color="text.secondary">
                <strong>Installer:</strong> {requestData.installerName}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  // --- Content rendering ---

  const renderVisualStory = () => {
    if (loadingAppInfo) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (appInfoError) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="error">{appInfoError}</Typography>
        </Box>
      );
    }
    if (!appInfo) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="text.secondary">No inspection data available</Typography>
        </Box>
      );
    }
    return (
      <VisualStoryTimeline
        appInfo={appInfo}
        runId={activeInspectionRunId}
        readOnlyFromUrl={readOnlyFromUrl}
        hideReadOnlyToggle={readOnlyFromUrl}
      />
    );
  };

  const renderLog = () => (
    <LogViewer
      runId={activeAutowatchRunId}
      readOnlyFromUrl={readOnlyFromUrl}
      currentUserProp={currentUser}
    />
  );

  const renderContent = () => {
    if (!selectedRequestId && isAuthenticated) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="text.secondary">Select a request from the sidebar</Typography>
        </Box>
      );
    }

    if (selectedError) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6" color="error">Failed to load request</Typography>
          <Typography variant="body2" color="text.disabled">{selectedError}</Typography>
        </Box>
      );
    }

    if (!hasVisualStory && !hasLog) {
      if (loadingSelected) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">Loading request...</Typography>
          </Box>
        );
      }
      if (selectedData) {
        const status = (selectedData.status || '').toLowerCase();
        const isWaiting = status === 'queued' || status === 'pending' || status === 'submitted';
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
            {isWaiting ? (
              <>
                <CircularProgress size={28} color="info" />
                <Typography variant="h6" color="text.secondary">Queued for inspection</Typography>
                <Typography variant="body2" color="text.disabled">
                  This request is waiting to be picked up. Results will appear here once inspection begins.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" color="text.secondary">No results yet</Typography>
                <Typography variant="body2" color="text.disabled">
                  {selectedData.status ? `Status: ${selectedData.status}` : 'Results are not available for this request.'}
                </Typography>
              </>
            )}
          </Box>
        );
      }
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="text.secondary">No results available</Typography>
        </Box>
      );
    }

    const contentInner = hasBothTabs ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}>
          <Tab label="Visual Story" sx={{ minHeight: 36, textTransform: 'none' }} />
          <Tab label="Watch/Inspect" sx={{ minHeight: 36, textTransform: 'none' }} />
        </Tabs>
        <Box sx={{ flex: 1, overflow: 'auto', display: activeTab === 0 ? 'block' : 'none' }}>
          {renderVisualStory()}
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', display: activeTab === 1 ? 'block' : 'none' }}>
          {renderLog()}
        </Box>
      </Box>
    ) : hasVisualStory ? (
      renderVisualStory()
    ) : hasLog ? (
      renderLog()
    ) : null;

    return (
      <Box sx={{ position: 'relative', height: '100%' }}>
        {loadingSelected && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 10,
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            flexDirection: 'column', gap: 1,
          }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">Loading request...</Typography>
          </Box>
        )}
        {contentInner}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {isAuthenticated ? renderAuthenticatedSidebar() : renderAnonymousSidebar()}
      <Box sx={{ flex: 1, minWidth: 0, height: '100vh', overflow: 'auto', bgcolor: 'background.default' }}>
        {renderContent()}
      </Box>

      {/* Re-Queue Modal */}
      <Dialog
        open={requeueModalOpen}
        onClose={() => {
          setRequeueModalOpen(false);
          setRequestToRequeue(null);
          setRequeueDoAutomation(false);
          setRequeuePriority(5);
          setRequeueError(null);
        }}
      >
        <DialogTitle>Re-Queue Inspection</DialogTitle>
        <DialogContent>
          {requestToRequeue && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                {requestToRequeue.AppName || 'Unknown App'}
                {requestToRequeue.AppVersion && (
                  <Typography component="span" variant="body2" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 400 }}>
                    v{requestToRequeue.AppVersion}
                  </Typography>
                )}
              </Typography>
            </Box>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            This will requeue the app for processing. A new autowatch run will be created and added to the queue. Continue?
          </DialogContentText>
          {requeueError && (
            <Box sx={{ mb: 2 }}>
              <Typography color="error" variant="body2">{requeueError}</Typography>
            </Box>
          )}
          <TextField
            type="number"
            label="Priority"
            value={requeuePriority}
            onChange={(e) => setRequeuePriority(Number(e.target.value))}
            inputProps={{ min: 1, max: 10 }}
            helperText="Higher number = higher priority (default: 5)"
            fullWidth
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={requeueDoAutomation}
                onChange={(e) => setRequeueDoAutomation(e.target.checked)}
              />
            }
            label="Do Automation"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRequeueModalOpen(false);
              setRequestToRequeue(null);
              setRequeueDoAutomation(false);
              setRequeuePriority(5);
              setRequeueError(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button onClick={handleRequeueConfirm} color="primary" variant="contained">
            Re-Queue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
